class VideoElement extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');
    this.observer = null;
    this.sources = Array.from(this.video?.querySelectorAll('source') || []);
    this.toggleSound = this.querySelector('button');
    this.handleSoundToggle = this.handleSoundToggle.bind(this);

    // Store original source URLs from data-src attribute
    this.sourceUrls = this.sources.map(source => {
      const url = source.getAttribute('data-src');
      return {
        element: source,
        url: url,
        media: source.media
      };
    });
  }

  connectedCallback() {
    if (!this.video) return;

    this.mutedStorageKey = `video_muted_${encodeURIComponent(this.id)}`;

    if (this.toggleSound) {
      this.toggleSound.addEventListener('click', this.handleSoundToggle);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.toggleSound && sessionStorage.getItem(this.mutedStorageKey) !== null) {
              sessionStorage.removeItem(this.mutedStorageKey);
              this.setMuted(false);
            }

            // Load video sources when coming into view
            if (!this.video.hasAttribute('data-loaded')) {
              this.sourceUrls.forEach(({element, url}) => {
                element.src = url;
              });
              this.video.load();
              this.video.setAttribute('data-loaded', 'true');
            }

            this.playVideo();
          } else {
            this.pauseVideo();
          }
        });
      },
      {
        // Start loading slightly before it comes into view
        // threshold: 0.1,
        rootMargin: '50px 0px'
      }
    );

    // Start observing the video element
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.toggleSound?.removeEventListener('click', this.handleSoundToggle);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  handleSoundToggle() {
    this.setMuted(!this.video.muted);
  }

  setMuted(muted) {
    if (!this.toggleSound) return;

    this.video.muted = muted;
    this.toggleSound.setAttribute('aria-pressed', String(!muted));
  }

  playVideo() {
    this.video.play().catch((error) => {
      console.warn('Auto-play failed:', error);
    });
  }

  pauseVideo() {
    this.video.pause();
    if (!this.toggleSound) return;

    if (!this.video.muted) {
      sessionStorage.setItem(this.mutedStorageKey, this.id);
    }
    this.setMuted(true);
  }
}

customElements.define('video-element', VideoElement);
